import type { ProjectAnnotation, ProjectAnnotationTargetType } from '@/types/project-annotations';
import type { AnnotationView, ProjectAnnotationStatus } from '@/types/project-annotations';
import {
  ANNOTATION_STATUS_VALUES,
  getAnnotationStatusLabel,
} from '@/components/features/project/annotations/annotation-panel-utils';

export type AnnotationStatusFilterValue = `${ProjectAnnotationStatus}`;

export type AnnotationStatusFilter = 'all' | readonly AnnotationStatusFilterValue[];

export const ANNOTATION_STATUS_FILTER_VALUES = ANNOTATION_STATUS_VALUES.map(
  (value) => String(value) as AnnotationStatusFilterValue
);

export const ANNOTATION_STATUS_FILTER_OPTIONS = [
  { value: 'all' as const, label: '全部状态' },
  { value: '1' as const, label: '待处理' },
  { value: '2' as const, label: '已修改' },
  { value: '3' as const, label: '已完成' },
  { value: '4' as const, label: '已关闭' },
];

export function getAnnotationStatusFilterOptions(view: AnnotationView) {
  return ANNOTATION_STATUS_FILTER_VALUES.map((value) => ({
    value,
    label: getAnnotationStatusLabel(Number(value) as ProjectAnnotationStatus, view),
  }));
}

export function isAllAnnotationStatusFilter(filter: AnnotationStatusFilter): filter is 'all' {
  return filter === 'all';
}

export function normalizeAnnotationStatusFilter(
  filter: AnnotationStatusFilter
): AnnotationStatusFilter {
  if (isAllAnnotationStatusFilter(filter)) return 'all';
  const uniqueValues = [...new Set(filter)].sort();
  if (uniqueValues.length === 0 || uniqueValues.length === ANNOTATION_STATUS_FILTER_VALUES.length) {
    return 'all';
  }
  return uniqueValues;
}

export function getDefaultStatusFilterForAnnotationView(
  view: AnnotationView
): AnnotationStatusFilter {
  return view === 'received' ? ['1'] : ['1', '2'];
}

export function isDefaultAnnotationStatusFilter(
  view: AnnotationView,
  filter: AnnotationStatusFilter
): boolean {
  const normalized = normalizeAnnotationStatusFilter(filter);
  const defaultFilter = normalizeAnnotationStatusFilter(
    getDefaultStatusFilterForAnnotationView(view)
  );

  if (isAllAnnotationStatusFilter(normalized) && isAllAnnotationStatusFilter(defaultFilter)) {
    return true;
  }
  if (isAllAnnotationStatusFilter(normalized) || isAllAnnotationStatusFilter(defaultFilter)) {
    return false;
  }

  return (
    normalized.length === defaultFilter.length &&
    normalized.every((value, index) => value === defaultFilter[index])
  );
}

export function hasCustomAnnotationStatusFilter(
  view: AnnotationView,
  filter: AnnotationStatusFilter
): boolean {
  const normalized = normalizeAnnotationStatusFilter(filter);
  if (isAllAnnotationStatusFilter(normalized)) return false;
  return !isDefaultAnnotationStatusFilter(view, normalized);
}

export function getAnnotationStatusFilterDisplayLabel(
  filter: AnnotationStatusFilter,
  view: AnnotationView
): string {
  const normalized = normalizeAnnotationStatusFilter(filter);
  if (isAllAnnotationStatusFilter(normalized)) return '全部状态';

  const labels = normalized.map((value) =>
    getAnnotationStatusLabel(Number(value) as ProjectAnnotationStatus, view)
  );
  if (labels.length <= 2) return labels.join('、');
  return `${labels[0]}等 ${labels.length} 项`;
}

export function isAnnotationStatusSelected(
  filter: AnnotationStatusFilter,
  status: AnnotationStatusFilterValue
): boolean {
  if (isAllAnnotationStatusFilter(filter)) return true;
  return filter.includes(status);
}

export function toggleAnnotationStatusFilter(
  filter: AnnotationStatusFilter,
  status: AnnotationStatusFilterValue
): AnnotationStatusFilter {
  const normalized = normalizeAnnotationStatusFilter(filter);

  if (isAllAnnotationStatusFilter(normalized)) {
    return normalizeAnnotationStatusFilter(
      ANNOTATION_STATUS_FILTER_VALUES.filter((value) => value !== status)
    );
  }

  if (normalized.includes(status)) {
    if (normalized.length === 1) {
      return normalized;
    }
    return normalizeAnnotationStatusFilter(normalized.filter((value) => value !== status));
  }

  return normalizeAnnotationStatusFilter([...normalized, status]);
}

export function buildAnnotationStatusFilterQueryParams(filter: AnnotationStatusFilter): {
  status?: number;
  statuses?: string;
} {
  const normalized = normalizeAnnotationStatusFilter(filter);
  if (isAllAnnotationStatusFilter(normalized)) return {};

  if (normalized.length === 1) {
    return { status: Number(normalized[0]) };
  }

  return { statuses: normalized.join(',') };
}

export type AnnotationTargetCategoryFilter =
  | 'all'
  | 'storyboard'
  | 'storyboard_image'
  | 'storyboard_video'
  | 'resource'
  | 'dialogue'
  | 'voiceover'
  | 'sound_effect';

export const ANNOTATION_TARGET_CATEGORY_FILTER_OPTIONS = [
  { value: 'all' as const, label: '全部类型' },
  { value: 'storyboard' as const, label: '分镜稿' },
  { value: 'storyboard_image' as const, label: '分镜图' },
  { value: 'storyboard_video' as const, label: '分镜视频' },
  { value: 'resource' as const, label: '资源' },
  { value: 'dialogue' as const, label: '对白' },
  { value: 'voiceover' as const, label: '配音' },
  { value: 'sound_effect' as const, label: '音效' },
] as const;

export interface ProjectAnnotationListFilters {
  status: AnnotationStatusFilter;
  targetCategory: AnnotationTargetCategoryFilter;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled annotation target type: ${String(value)}`);
}

export function resolveAnnotationTargetType(
  annotation: ProjectAnnotation
): ProjectAnnotationTargetType {
  return annotation.metadata.annotationTarget?.kind ?? annotation.targetType;
}

export function resolveAnnotationTargetCategory(
  annotation: ProjectAnnotation
): AnnotationTargetCategoryFilter | 'none' {
  const targetType = resolveAnnotationTargetType(annotation);
  switch (targetType) {
    case 'storyboard':
    case 'storyboard_item':
      return 'storyboard';
    case 'storyboard_image':
      return 'storyboard_image';
    case 'storyboard_video':
      return 'storyboard_video';
    case 'project_resource':
    case 'resource_element':
    case 'resource_tag':
      return 'resource';
    case 'dialogue':
      return 'dialogue';
    case 'voiceover':
      return 'voiceover';
    case 'sound_effect':
      return 'sound_effect';
    case 'none':
      return 'none';
  }
  return assertNever(targetType);
}

export function matchesAnnotationStatusFilter(
  annotation: ProjectAnnotation,
  status: AnnotationStatusFilter
): boolean {
  const normalized = normalizeAnnotationStatusFilter(status);
  if (isAllAnnotationStatusFilter(normalized)) return true;
  return normalized.includes(String(annotation.status) as AnnotationStatusFilterValue);
}

export function matchesAnnotationTargetCategoryFilter(
  annotation: ProjectAnnotation,
  targetCategory: AnnotationTargetCategoryFilter
): boolean {
  if (targetCategory === 'all') return true;
  return resolveAnnotationTargetCategory(annotation) === targetCategory;
}

export function filterProjectAnnotations(
  annotations: readonly ProjectAnnotation[],
  filters: ProjectAnnotationListFilters
): ProjectAnnotation[] {
  return annotations.filter(
    (annotation) =>
      matchesAnnotationStatusFilter(annotation, filters.status) &&
      matchesAnnotationTargetCategoryFilter(annotation, filters.targetCategory)
  );
}
